import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import type { Table, Order } from '@/types/common';

interface TableWithOrder extends Table {
  currentOrder?: Order;
}

export default function TablesPage() {
  const [tables, setTables] = useState<TableWithOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  // Fetch tables and their current orders
  useEffect(() => {
    const fetchTables = async () => {
      try {
        // Fetch tables
        const { data: tablesData, error: tablesError } = await supabase
          .from('tables')
          .select('*')
          .order('number');

        if (tablesError) throw tablesError;

        // Fetch active orders for occupied tables
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .in('status', ['pending', 'preparing', 'ready', 'delivered']);

        if (ordersError) throw ordersError;

        // Combine tables with their current orders
        const tablesWithOrders = tablesData.map(table => ({
          ...table,
          currentOrder: ordersData?.find(order => order.table_id === table.id),
        }));

        setTables(tablesWithOrders);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tables:', error);
        addToast({
          type: 'error',
          message: 'Failed to load tables',
        });
      }
    };

    fetchTables();

    // Set up real-time subscription
    const subscription = supabase
      .channel('tables-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tables'
      }, () => {
        fetchTables();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [addToast]);

  // Handle table status update
  const updateTableStatus = async (tableId: string, status: Table['status']) => {
    try {
      const { error } = await supabase
        .from('tables')
        .update({ status })
        .eq('id', tableId);

      if (error) throw error;

      addToast({
        type: 'success',
        message: `Table status updated to ${status}`,
      });
    } catch (error) {
      console.error('Error updating table status:', error);
      addToast({
        type: 'error',
        message: 'Failed to update table status',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen-75">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-4">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
            Tables Overview
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-dark-text-secondary">
            Manage restaurant tables and their current status
          </p>
        </div>

        {/* Tables Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tables.map((table) => (
            <div
              key={table.id}
              className="relative bg-white dark:bg-dark-bg-light rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-4">
                {/* Table Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                    Table #{table.number}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      table.status === 'available'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                        : table.status === 'occupied'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
                    }`}
                  >
                    {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
                  </span>
                </div>

                {/* Table Info */}
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
                    <i className="fas fa-users mr-2" />
                    {table.seats} seats
                  </p>
                  {table.currentOrder && (
                    <p className="text-sm text-gray-500 dark:text-dark-text-secondary">
                      <i className="fas fa-clock mr-2" />
                      Order #{table.currentOrder.id.slice(0, 8)}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-4 flex justify-between items-center">
                  {table.status === 'available' ? (
                    <Link
                      to={`/orders/new?table=${table.id}`}
                      className="inline-flex items-center justify-center px-3 py-1.5 text-base font-semibold text-white rounded-lg transition-all duration-300 ease-out bg-gradient-to-r from-cyan-500 to-blue-500 shadow-[0_4px_0_0_#0369a1] hover:shadow-[0_6px_0_0_#0369a1] hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-[0_2px_0_0_#0369a1] touch-manipulation"
                    >
                      <i className="fas fa-plus mr-2" />
                      New Order
                    </Link>
                  ) : table.currentOrder ? (
                    <Link
                      to={`/orders/${table.currentOrder.id}`}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-dark-border text-sm font-medium rounded-md text-gray-700 dark:text-dark-text-primary bg-white dark:bg-dark-bg-light hover:bg-gray-50 dark:hover:bg-dark-bg-lighter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <i className="fas fa-eye mr-2" />
                      View Order
                    </Link>
                  ) : null}

                  {/* Status Actions */}
                  <div className="flex space-x-2">
                    {table.status !== 'available' && (
                      <button
                        onClick={() => updateTableStatus(table.id, 'available')}
                        className="inline-flex items-center p-1.5 border border-transparent text-sm font-medium rounded-md text-green-700 dark:text-green-200 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        title="Mark as available"
                      >
                        <i className="fas fa-check" />
                      </button>
                    )}
                    {table.status !== 'reserved' && (
                      <button
                        onClick={() => updateTableStatus(table.id, 'reserved')}
                        className="inline-flex items-center p-1.5 border border-transparent text-sm font-medium rounded-md text-yellow-700 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900/30 hover:bg-yellow-200 dark:hover:bg-yellow-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                        title="Mark as reserved"
                      >
                        <i className="fas fa-clock" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {tables.length === 0 && (
          <div className="text-center py-12">
            <i className="fas fa-chair text-4xl text-gray-400 dark:text-dark-text-secondary mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">
              No tables configured
            </h3>
            <p className="text-gray-500 dark:text-dark-text-secondary">
              Contact your administrator to set up tables.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}